// Kyte final-checkout call (POST kyte.checkout). For ancillariesAsStep
// providers the booking is already held server-side by book_held; this is the
// payment commit against that hold. The success response carries
// `kyteVoucherId`; checkout.vue reads `flightVoucherId` (Amadeus convention)
// so we copy the id across before resolving to keep the view provider-agnostic.

import { service_handler } from '@/services';
import { log } from '@/utilities/commons';
import {
  normalize_booking,
  build_baggage_allowance_from_offer,
} from './normalize';
import { extract_dictionaries } from './dictionaries';
import { as_string, as_array, merge_dictionaries } from '../base/helpers';
import { fold_committed_ancillaries } from './ancillaries';

export const book = (_provider, flight_offer, request) => {
  const {
    heldBooking = {},
    guest = {},
    payments = {},
    livePrice = '',
  } = request || {};
  const { validatingAirlineCodes = [], providerOfferId = '' } =
    flight_offer || {};
  // baggageAllowance isn't echoed back in /retrieve (only /details ships it),
  // so we stash it on the booking via additionalInfo and re-read it in
  // get_confirmation — keeps the confirm page brand/allowance honest without
  // a second /details fetch.
  const baggage_allowance = build_baggage_allowance_from_offer(flight_offer);
  const booking_data = {
    bookingId: heldBooking.id || '',
    airlineCode:
      validatingAirlineCodes.length > 0 ? validatingAirlineCodes[0] : '',
    offerId: providerOfferId,
    live_price: livePrice,
    payments,
  };
  if (baggage_allowance.length) {
    booking_data.additionalInfo = { baggageAllowance: baggage_allowance };
  }
  const body = {
    ...guest,
    bookingData: booking_data,
  };
  return service_handler('kyte.checkout', body, { track: 'checkout' }).then(
    (response) => {
      const { data = {} } = response || {};
      if (
        data.kyteVoucherId !== undefined &&
        data.flightVoucherId === undefined
      ) {
        // Return a fresh envelope — don't mutate caller-visible state.
        return {
          ...response,
          data: { ...data, flightVoucherId: data.kyteVoucherId },
        };
      }
      return response;
    }
  );
};

/**
 * Reshape Kyte's flat `firstName/lastName` + `contactInformation.{email,
 * phone[]}` into the Amadeus-shaped traveler (`name.*`, `contact.*`)
 * confirm.vue expects. Normalizes once at the boundary so the page stays
 * provider-agnostic.
 */
const normalize_traveler = (pax) => {
  const safe = pax || {};
  const contact = safe.contactInformation || {};
  const phone_in = Array.isArray(contact.phone) ? contact.phone : [];
  const phones = phone_in.map((p) => {
    const safe_p = p || {};
    return {
      countryCallingCode: as_string(safe_p.countryCode),
      number: as_string(safe_p.number),
      type: as_string(safe_p.type) || 'Mobile',
    };
  });
  return {
    id: as_string(safe.id),
    dateOfBirth: as_string(safe.dateOfBirth),
    gender: as_string(safe.gender),
    title: as_string(safe.title),
    type: as_string(safe.type),
    name: {
      firstName: as_string(safe.firstName),
      middleName: as_string(safe.middleName),
      lastName: as_string(safe.lastName),
    },
    contact: {
      emailAddress: as_string(contact.email),
      phones,
    },
  };
};

/**
 * Surface the canonical confirmation shape from a /confirm response body.
 * Aliases upstream `kyteVoucher` to `voucher` (Amadeus convention), caches
 * dictionaries on the provider, projects the booking onto a canonical
 * FlightOffer via normalize_booking, and exposes Amadeus-shaped travelers.
 * @param {object} provider
 * @param {object} body
 * @returns {object}
 */
export const get_confirmation = (provider, body) => {
  const safe_body = body || {};
  const { kyteVoucher: raw_voucher = {}, ...rest } = safe_body;
  const { orderData = {}, pnr, ...voucher_root } = raw_voucher;
  const { booking = {}, additionalInfo: order_info } = orderData;
  merge_dictionaries(provider, extract_dictionaries(safe_body));

  let flight_offers = [];
  if (booking && booking.id) {
    // cancellara echoes the stashed additionalInfo at orderData level (a
    // sibling of booking), not nested inside the booking node — read it from
    // orderData and fall back to booking so the allowance survives either shape.
    const echoed = order_info || booking.additionalInfo || {};
    const { baggageAllowance: echoed_allowance = [] } = echoed;
    try {
      const normalized = normalize_booking(provider, booking, {
        baggageAllowance: as_array(echoed_allowance),
      });
      flight_offers = [fold_committed_ancillaries(normalized, booking)];
    } catch (e) {
      // Log BE-side regressions; fall through with an empty list rather than
      // crash the confirm page (the rest of the voucher metadata is useful).
      log('[kyte/checkout] get_confirmation: normalize failed', e);
      flight_offers = [];
    }
  }

  const travelers = Array.isArray(booking.passengers)
    ? booking.passengers.map(normalize_traveler)
    : [];

  return {
    ...rest,
    voucher: {
      ...voucher_root,
      flight_offers,
      travelers,
      tickets: Array.isArray(booking.tickets) ? booking.tickets : [],
      carrierReference: pnr,
    },
  };
};
